/* eslint complexity: 0 */
import {Alert, Divider} from "antd";
import {ApolloError} from "@apollo/client";
import {FormikProps} from "formik";
import React, {FC, memo} from "react";
import {useGetLocationsSortedByCityQuery, useGetDetailedCourseTypesQuery, useGetCourseTypeStateQuery, Maybe, useGetCompanyInfoQuery} from "../../generated/graphql";
import i18n from "../../services/i18n";
import LayoutStep from "../../views/LayoutStep/LayoutStep";
import {prepareLocationsForCascader} from "./courseHelpers";
import {CourseAllowLessonBookingCheckbox, CourseIsActiveCheckbox, CourseLocationInput, CourseShowInWebCheckbox, CourseTypeInput, OnlineBookingBlockedCheckbox} from "./CourseViews/CourseStepViews";
import GeneratedNumberBadge from "../../views/GeneratedNumberBadge/GeneratedNumberBadge";
import {CourseTypeStateData, PossibleFormikPropsType} from "./CourseCreate/CourseCreateForm";

type StepCourseDataProps = {
    formikProps: FormikProps<PossibleFormikPropsType>;
    onNextStep: () => void;
    error: ApolloError | Error | undefined;
    courseNumber?: string;
    nextStepButton: React.ReactElement;
    subtitle?: Maybe<string> | React.ReactNode;
    hasBilledLessons?: boolean;
};

const StepCourseData: FC<StepCourseDataProps> = ({formikProps, onNextStep, error: errorInProps, courseNumber, nextStepButton, subtitle, hasBilledLessons = false}) => {
    // const [selectedCourseTypeId, setSelectedCourseTypeId] = useState<string | undefined>()
    const {data: courseTypesResult, loading: loadingTypes, error: errorInTypes} = useGetDetailedCourseTypesQuery();

    const courseTypeId = formikProps.values.courseTypeId;

    const {
        data: courseTypeState,
        loading: loadingCourseTypeState,
        refetch: refetchCourseTypeState,
    } = useGetCourseTypeStateQuery({
        variables: {id: courseTypeId ?? ""},
        skip: !courseTypeId,
    });

    const courseTypeStateData: CourseTypeStateData = {
        data: courseTypeState?.courseTypeStateById,
        loading: loadingCourseTypeState,
        refetch: refetchCourseTypeState,
    };

    const courseTypes = courseTypesResult === undefined ? [] : courseTypesResult.courseTypes;

    const {data: locationsSortedByCityResult, error: errorLocationsByCity} = useGetLocationsSortedByCityQuery();

    let error: ApolloError | Error | undefined;

    if (errorInTypes !== undefined) {
        error = errorInTypes;
    }

    if (errorLocationsByCity !== undefined) {
        error = errorLocationsByCity;
    }

    if (errorInProps !== undefined) {
        error = errorInProps;
    }

    const {locationId, venueId} = formikProps.values;
    const locationsData = locationsSortedByCityResult?.locationsSortedByCity ?? [];

    const cityGroup = locationsData.find((locationData) => locationData.locations.some(({id}) => id === locationId));

    const location = cityGroup?.locations.find(({id}) => id === locationId);
    const venue = location?.venues.find(({id}) => id === venueId);

    const cascaderValue = cityGroup !== undefined && location !== undefined && venue !== undefined ? [cityGroup.city, location.id, venue.id] : undefined;

    const {data: companyInfoData} = useGetCompanyInfoQuery();
    const featureLessonBooking = companyInfoData?.companyInfo?.subscription?.featureLessonBooking;

    return (
        <LayoutStep
            columns={16}
            title={i18n.containers.courses.CourseCreate.courseStepData.title()}
            subtitle={subtitle}
            button={nextStepButton}
            error={error}
            lessonBooking={Boolean(formikProps.values.allowLessonBooking)}
        >
            <CourseIsActiveCheckbox label="Kurs aktiv" formikProps={formikProps} testId="CourseIsActiveCheckbox" style={{gridColumn: "span 4"}} />

            {featureLessonBooking && (
                <CourseAllowLessonBookingCheckbox
                    label={i18n.containers.courses.Courses.allowLessonBooking.label()}
                    formikProps={formikProps}
                    testId="CourseAllowLessonBookingCheckbox"
                    style={{gridColumn: "span 4"}}
                />
            )}
            <OnlineBookingBlockedCheckbox
                label="Onlinebuchungen blockieren"
                formikProps={formikProps}
                testId="OnlineBookingBlockedCheckbox"
                defaultChecked={courseTypeStateData?.data?.isActive}
                style={{gridColumn: "span 4"}}
            />
            <CourseShowInWebCheckbox
                label="Im Web anzeigen"
                formikProps={formikProps}
                testId="CourseShowInWebCheckbox"
                defaultChecked={courseTypeStateData?.data?.isActive}
                style={{gridColumn: "span 4"}}
            />
            <Divider style={{gridColumn: "1 / -1", marginTop: 5, marginBottom: 30}} />
            <CourseTypeInput
                courseTypes={Array.isArray(courseTypes) ? courseTypes : []}
                label={i18n.containers.courses.CourseCreate.courseType.label()}
                placeholder={i18n.containers.courses.CourseCreate.courseType.placeholder()}
                addNewCourseTypeText={i18n.containers.courses.CourseCreate.courseType.addNewCourseType()}
                formikProps={formikProps}
                isLoading={loadingTypes}
                testId="CourseTypeInput"
                style={{gridColumn: "span 8"}}
                disabled={true}
            />
            <GeneratedNumberBadge number={courseNumber} type="courseNumber" formikProps={formikProps} style={{gridColumn: "9 / span 4"}} />
            {hasBilledLessons && (
                <Alert
                    type="info"
                    showIcon
                    message={i18n.containers.courses.CourseEdit.billedLessons.settingsDisabled.courseData()}
                    style={{marginBottom: 16, gridColumn: "1 / -1"}}
                />
            )}
            <CourseLocationInput
                options={prepareLocationsForCascader(locationsSortedByCityResult?.locationsSortedByCity ?? [])}
                label={i18n.containers.courses.CourseCreate.courseLocation.label()}
                placeholder={i18n.containers.courses.CourseCreate.courseLocation.placeholder()}
                formikProps={formikProps}
                value={cascaderValue}
                testId="CourseLocationInput"
                disabled={hasBilledLessons}
            />
        </LayoutStep>
    );
};

export default memo(StepCourseData);
